import * as React from "react";
import {
  createBrowserRouter,
  Link,
  Navigate,
  RouterProvider,
  type RouteObject,
} from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import type { Permission } from "@poji/shared";
import { Button } from "@/components/ui/button";
import { AppShell } from "@/components/app-shell";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ApiError } from "@/lib/api";
import { DashboardPage } from "@/pages/dashboard";
import { TaskDetailPage, TasksPage } from "@/pages/tasks";
import { UserDetailPage, UsersPage } from "@/pages/users";
import { ProviderDetailPage, ProvidersPage } from "@/pages/providers";
import { DocumentsPage } from "@/pages/documents";
import { MissingReceiptsPage } from "@/pages/missing-receipts";
import { SeriesPage } from "@/pages/series";
import { ApprovalsPage, DisputeDetailPage, DisputesPage } from "@/pages/disputes";
import { MessagesPage, ReportsPage } from "@/pages/moderation";
import { ServiceRequestsPage } from "@/pages/service-requests";
import { CatalogPage } from "@/pages/catalog";
import { FeesPage } from "@/pages/fees";
import { PromotionsPage } from "@/pages/promotions";
import { StaffPage } from "@/pages/staff";
import { TranslationsPage } from "@/pages/translations";
import { MarketSettingsPage } from "@/pages/market-settings";
import { FormDetailPage, FormsPage } from "@/pages/forms";
import { CancellationPage } from "@/pages/cancellation";
import { CmsPageEditorPage, CmsPagesPage } from "@/pages/cms-pages";
import { AuditPage } from "@/pages/audit";
import { GeoPage } from "@/pages/geo";
import { AccountingPage, PayoutsPage } from "@/pages/finance";
import { FullPageSpinner, LoginPage, MfaPage, NotStaffPage, SetPasswordPage } from "@/pages/auth";
import { ErrorState } from "@/components/common";

/** Yetkisi olmayan ekran: menüde zaten görünmez; adres elle yazılırsa açıklama */
function Guard({ permission, children }: { permission: Permission; children: React.ReactNode }) {
  const { can } = useAuth();
  if (!can(permission))
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <h1 className="text-lg font-semibold">Bu ekran için yetkiniz yok</h1>
        <p className="mt-2 text-sm text-muted-foreground">Gerekli yetki: {permission}</p>
        <Button asChild variant="outline" className="mt-6">
          <Link to="/">Genel bakışa dön</Link>
        </Button>
      </div>
    );
  return <>{children}</>;
}

const g = (permission: Permission, el: React.ReactNode) => (
  <Guard permission={permission}>{el}</Guard>
);

export const routes: RouteObject[] = [
  {
    element: <AppShell />,
    errorElement: <ErrorState error={new Error("Sayfa açılamadı.")} />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "tasks", element: g("tasks.view", <TasksPage />) },
      { path: "tasks/:id", element: g("tasks.view", <TaskDetailPage />) },
      { path: "missing-receipts", element: g("tasks.view", <MissingReceiptsPage />) },
      { path: "series", element: g("tasks.view", <SeriesPage />) },
      { path: "users", element: g("users.view", <UsersPage />) },
      { path: "users/:id", element: g("users.view", <UserDetailPage />) },
      { path: "disputes", element: g("tasks.disputes.manage", <DisputesPage />) },
      { path: "disputes/:id", element: g("tasks.disputes.manage", <DisputeDetailPage />) },
      { path: "approvals", element: g("staff.approvals.decide", <ApprovalsPage />) },
      { path: "providers", element: g("providers.applications.review", <ProvidersPage />) },
      {
        path: "providers/:id",
        element: g("providers.applications.review", <ProviderDetailPage />),
      },
      { path: "documents", element: g("providers.verification.manage", <DocumentsPage />) },
      { path: "reports", element: g("support.reports.moderate", <ReportsPage />) },
      { path: "messages", element: g("support.reports.moderate", <MessagesPage />) },
      { path: "service-requests", element: g("catalog.services.manage", <ServiceRequestsPage />) },
      { path: "catalog", element: g("catalog.services.manage", <CatalogPage />) },
      { path: "fees", element: g("settings.fees.manage", <FeesPage />) },
      { path: "promotions", element: g("marketing.promotions.manage", <PromotionsPage />) },
      {
        path: "translations",
        element: g("catalog.translations.manage", <TranslationsPage />),
      },
      { path: "forms", element: g("catalog.forms.manage", <FormsPage />) },
      { path: "forms/:id", element: g("catalog.forms.manage", <FormDetailPage />) },
      { path: "settings", element: g("settings.markets.manage", <MarketSettingsPage />) },
      { path: "pages", element: g("cms.pages.edit_all", <CmsPagesPage />) },
      { path: "pages/:id", element: g("cms.pages.edit_all", <CmsPageEditorPage />) },
      { path: "cancellation", element: g("settings.markets.manage", <CancellationPage />) },
      { path: "geo", element: g("settings.markets.manage", <GeoPage />) },
      { path: "audit", element: g("staff.audit.view", <AuditPage />) },
      { path: "staff", element: g("staff.manage", <StaffPage />) },
      { path: "accounting", element: g("accounting.ledger.view", <AccountingPage />) },
      { path: "payouts", element: g("accounting.ledger.view", <PayoutsPage />) },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
];

const router = createBrowserRouter(routes);

/** Giriş durumuna göre: giriş → 2FA → panel */
function Gate() {
  const { state } = useAuth();
  switch (state.kind) {
    case "loading":
      return <FullPageSpinner />;
    case "signed_out":
      return <LoginPage />;
    case "set_password":
      return <SetPasswordPage />;
    case "not_staff":
      return <NotStaffPage />;
    case "needs_mfa":
      return <MfaPage />;
    case "error":
      return (
        <div className="p-8">
          <ErrorState error={state.error} onRetry={() => window.location.reload()} />
        </div>
      );
    case "ready":
      return <RouterProvider router={router} />;
  }
}

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        // yetki/bulunamadı hataları tekrar denenmez
        retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
      },
    },
  });
}

export function App() {
  const [qc] = React.useState(makeQueryClient);
  return (
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <Gate />
      </AuthProvider>
      <Toaster richColors position="top-right" closeButton />
    </QueryClientProvider>
  );
}
